import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ExternalLink, Briefcase, ChevronDown, MessageSquarePlus, Trash2, X } from "lucide-react";

const STAGES = [
  { id: "sent", label: "Sent", color: "hsl(var(--muted-foreground))", hint: "Submitted, waiting to hear back." },
  { id: "interviewing", label: "Interviewing", color: "hsl(var(--accent-amber))", hint: "In conversation - phone screen, on-site, take-home." },
  { id: "offer", label: "Offer", color: "hsl(var(--accent-green))", hint: "Verbal or written offer in hand." },
  { id: "closed", label: "Closed", color: "hsl(var(--primary))", hint: "Rejected, withdrew, or ghosted." },
];

const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]));

export default function SentApplications() {
  const [apps, setApps] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/applications")
      .then((r) => { if (alive) setApps(r.data.applications || []); })
      .catch(() => { if (alive) setApps([]); });
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const a of (apps || [])) {
      const st = a.user_job?.application_status || "sent";
      (out[st] || out.sent).push(a);
    }
    // Newest update first
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => new Date(b.user_job.updated_at || b.user_job.sent_at || 0) - new Date(a.user_job.updated_at || a.user_job.sent_at || 0));
    }
    return out;
  }, [apps]);

  const moveStage = async (job_id, nextStatus) => {
    // Optimistic update
    const snapshot = apps;
    setApps((curr) => curr.map((a) => a.user_job.job_id === job_id ? { ...a, user_job: { ...a.user_job, application_status: nextStatus, updated_at: new Date().toISOString() } } : a));
    try {
      await api.post(`/applications/${job_id}/status`, { status: nextStatus });
      toast.success(`Moved to ${STAGE_BY_ID[nextStatus]?.label}.`);
    } catch (e) {
      setApps(snapshot);
      toast.error("Couldn't update status.");
    }
  };

  const deleteApp = async (job_id) => {
    const snapshot = apps;
    setApps((curr) => curr.filter((a) => a.user_job.job_id !== job_id));
    try {
      await api.delete(`/applications/${job_id}`);
      toast.success("Removed from tracker.");
    } catch (e) {
      setApps(snapshot);
      toast.error("Couldn't remove application.");
    }
  };

  const saveNotes = async (job_id, notes) => {
    setApps((curr) => curr.map((a) => a.user_job.job_id === job_id ? { ...a, user_job: { ...a.user_job, application_notes: notes } } : a));
    try {
      const current = (apps || []).find((a) => a.user_job.job_id === job_id);
      await api.post(`/applications/${job_id}/status`, { status: current?.user_job?.application_status || "sent", notes });
      toast.success("Saved.");
    } catch {
      toast.error("Couldn't save notes.");
    }
  };

  if (!apps) {
    return <Layout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-10">
        <div className="terminal-label text-primary">[ APPLICATIONS.TRACKER ]</div>
        <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-2 mb-2">Sent</h1>
        <p className="text-sm text-muted-foreground mb-6 sm:mb-8">
          {apps.length} application{apps.length === 1 ? "" : "s"} tracked. Move between stages as you hear back. This is your private tracker - we never auto-submit.
        </p>

        {apps.length === 0 && (
          <div className="panel p-6 sm:p-12 text-center" data-testid="sent-empty-state">
            <Briefcase className="w-10 h-10 text-foreground/40 mx-auto mb-6" strokeWidth={1.25} />
            <div className="terminal-label mb-3">[ NO.APPLICATIONS.YET ]</div>
            <h2 className="serif-mod text-2xl tracking-tight mb-3" style={{ fontWeight: 500 }}>Your tracker is empty.</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
              Pick a job from your daily feed, review the AI-tailored materials, then mark it as sent once you&apos;ve submitted on the company&apos;s site. It will show up here so you can track interviews, offers, and outcomes.
            </p>
            <Link to="/dashboard" className="btn-primary" data-testid="go-to-feed">Browse today&apos;s matches →</Link>
          </div>
        )}

        {apps.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {STAGES.map((stage) => (
              <Column
                key={stage.id}
                stage={stage}
                cards={groups[stage.id] || []}
                onMove={moveStage}
                onSaveNotes={saveNotes}
                onDelete={deleteApp}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Column({ stage, cards, onMove, onSaveNotes, onDelete }) {
  return (
    <div className="panel p-0 flex flex-col min-h-[200px]" data-testid={`kanban-col-${stage.id}`}>
      <div className="px-4 py-3 border-b border-foreground/20 flex items-center justify-between gap-2 sticky top-0 bg-background z-10">
        <div className="min-w-0">
          <div className="terminal-label" style={{ color: stage.color }}>[ {stage.label.toUpperCase()} ]</div>
          <div className="text-[10px] text-muted-foreground mt-1 truncate">{stage.hint}</div>
        </div>
        <span className="font-mono text-xs tabular-nums px-2 py-0.5 border border-foreground/20 bg-surface flex-shrink-0">{cards.length}</span>
      </div>
      <div className="p-2 space-y-2 flex-1">
        {cards.length === 0 && (
          <div className="px-3 py-6 text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono">- empty -</div>
        )}
        {cards.map((c) => (
          <KanbanCard key={c.user_job.id} entry={c} stage={stage} onMove={onMove} onSaveNotes={onSaveNotes} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function KanbanCard({ entry, stage, onMove, onSaveNotes, onDelete }) {
  const { user_job, job } = entry;
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(user_job.application_notes || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const stampLabel = stage.id === "sent" ? "Sent" : "Updated";
  const stamp = user_job.updated_at || user_job.sent_at;

  return (
    <div className="border border-foreground/15 bg-background hover:border-primary/40 transition-colors p-3 group relative" data-testid={`kanban-card-${user_job.job_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[13px] uppercase font-semibold tracking-tight leading-tight break-words" title={job?.title}>{job?.title || "Unknown role"}</div>
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">{job?.company || ""}{job?.location ? ` · ${job.location}` : ""}</div>
        </div>
        <button
          onClick={() => setConfirmDelete(true)}
          className="text-muted-foreground/60 hover:text-primary p-1 -m-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
          title="Remove from tracker"
          data-testid="kanban-delete-trigger"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="mt-2 text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
        {stampLabel} {stamp ? new Date(stamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "-"}
      </div>

      {user_job.application_notes && !editingNotes && (
        <p className="mt-2 text-[11px] text-foreground/75 leading-snug whitespace-pre-wrap break-words border-l-2 border-foreground/15 pl-2" data-testid="kanban-notes">{user_job.application_notes}</p>
      )}

      {editingNotes && (
        <div className="mt-2">
          <textarea
            data-testid="kanban-notes-input"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="e.g. phone screen Mon 3pm with Lisa"
            className="input-base text-[11px] w-full"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => { setEditingNotes(false); onSaveNotes(user_job.job_id, notesDraft); }}
              className="btn-primary text-[10px]"
              data-testid="kanban-notes-save"
            >Save</button>
            <button
              onClick={() => { setEditingNotes(false); setNotesDraft(user_job.application_notes || ""); }}
              className="btn-ghost text-[10px]"
            >Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-foreground/10 flex flex-wrap items-center gap-2 justify-between">
        <StatusDropdown current={stage.id} onChange={(next) => onMove(user_job.job_id, next)} />
        <div className="flex items-center gap-1">
          {!editingNotes && (
            <button
              onClick={() => setEditingNotes(true)}
              className="text-muted-foreground hover:text-primary p-1"
              title={user_job.application_notes ? "Edit note" : "Add a note"}
              data-testid="kanban-notes-edit"
            ><MessageSquarePlus className="w-3.5 h-3.5" /></button>
          )}
          {user_job.share_slug && (
            <Link to={`/p/${user_job.share_slug}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary p-1" title="Open shareable portfolio">
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
          <Link to={`/applications/${user_job.job_id}`} className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted-foreground hover:text-primary px-1.5 py-1">
            View
          </Link>
        </div>
      </div>

      {confirmDelete && (
        <DeleteConfirmModal
          job={job}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => { setConfirmDelete(false); onDelete(user_job.job_id); }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({ job, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      data-testid="kanban-delete-modal"
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-md p-6">
        <button onClick={onCancel} aria-label="Close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
        <div className="terminal-label text-primary mb-3">[ REMOVE.APPLICATION ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-3">Remove this from your tracker?</h3>
        <p className="text-sm text-foreground/80 leading-relaxed mb-4">
          You&apos;re about to remove <span className="font-medium text-foreground">{job?.title || "this application"}</span>{job?.company ? <> at <span className="font-medium text-foreground">{job.company}</span></> : null} from your kanban.
        </p>
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-6">
          Your tailored resume, cover letter, and portfolio for this role stay saved, and the job will reappear in your feed. Only the tracker card and any notes you added here will be cleared. This can&apos;t be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="btn-ghost text-[11px]" data-testid="kanban-delete-cancel">Cancel</button>
          <button
            onClick={onConfirm}
            className="btn-primary inline-flex items-center gap-2 text-[11px]"
            data-testid="kanban-delete-confirm"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusDropdown({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const here = STAGE_BY_ID[current];
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="kanban-status-trigger"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-mono px-2 py-1 border border-foreground/15 hover:border-primary transition-colors"
        style={{ color: here?.color }}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: here?.color }} />
        {here?.label}
        <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-40 panel py-1 min-w-[160px] shadow-xl">
            {STAGES.map((s) => (
              <button
                key={s.id}
                onClick={() => { onChange(s.id); setOpen(false); }}
                data-testid={`kanban-status-opt-${s.id}`}
                className={`w-full text-left px-3 py-2 text-[11px] hover:bg-surface-hover flex items-center gap-2 font-mono uppercase tracking-[0.18em] ${s.id === current ? "bg-surface" : ""}`}
                style={{ color: s.color }}
              >
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
